<template>
    <span
        class="NavigationIndicator"
        ref="indicator"
        :class="{ 'is-mobile': isMobile }"
    ></span>
</template>

<script>
import { gsap } from 'gsap';

function debounce(func, wait) {
    let timeout;
    return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

function getSpringEase(config = { amplitude: 0.3, period: 0.4 }) {
    if (config.type === 'back') {
        return `back.out(${config.overshoot || 1.2})`;
    }
    return `elastic.out(${config.amplitude || 0.3}, ${config.period || 0.4})`;
}

export default {
    name: 'NavigationIndicator',
    props: {
        isMobile: {
            type: Boolean,
            default: false,
        },
        isCollapsed: {
            type: Boolean,
            default: false,
        },
        drawerRoutes: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            currentPosition: { x: 0, y: 0 },
            navLinkPositions: {},
            positionsCalculated: false,
            mutationObserver: null,
            lastNavLinkCount: 0,
        };
    },
    computed: {
        isCurrentRouteInDrawer() {
            if (!this.isMobile) return false;
            return this.drawerRoutes.includes(this.$route.path);
        },
    },
    watch: {
        '$route.path': {
            handler() {
                this.$nextTick(() => this.animateToRoute());
            },
        },
        isMobile: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.handleLayoutChange();
                }
            },
        },
        isCollapsed: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.handleLayoutChange();
                }
            },
        },
    },
    mounted() {
        this.debouncedRecalculate = debounce(this.recalculate, 150);
        this.$nextTick(() => {
            setTimeout(() => {
                this.initializeIndicator();
                this.setupMutationObserver();
            }, 100);
        });
        window.addEventListener('resize', this.debouncedRecalculate);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.debouncedRecalculate);
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
    },
    methods: {
        initializeIndicator() {
            this.calculateNavPositions();
            this.animateToRoute(false);
        },

        setupMutationObserver() {
            const navContainer = this.getNavContainer();
            if (!navContainer || this.mutationObserver) return;

            this.mutationObserver = new MutationObserver(
                debounce(() => {
                    const links = navContainer.querySelectorAll('.Nav-Link');
                    if (links.length !== this.lastNavLinkCount) {
                        this.lastNavLinkCount = links.length;
                        this.positionsCalculated = false;
                        this.calculateNavPositions();
                        this.animateToRoute(false);
                    }
                }, 100),
            );

            this.mutationObserver.observe(navContainer, {
                childList: true,
                subtree: true,
            });
        },

        calculateNavPositions() {
            const navContainer = this.getNavContainer();
            if (!navContainer) return;

            const positions = {};
            const links = navContainer.querySelectorAll('.Nav-Link');
            const moreButton = navContainer.querySelector('.More-Button');
            const containerRect = navContainer.getBoundingClientRect();

            this.lastNavLinkCount = links.length;

            links.forEach((link) => {
                const href = link.getAttribute('href');
                if (href) {
                    const rect = link.getBoundingClientRect();

                    if (this.isMobile) {
                        positions[href] = {
                            x: rect.left + rect.width / 2 - containerRect.left,
                            y: 0,
                        };
                    } else {
                        positions[href] = {
                            x: 0,
                            y: rect.top - containerRect.top + 8,
                        };
                    }
                }
            });

            if (moreButton && this.isMobile) {
                const rect = moreButton.getBoundingClientRect();
                positions['__more__'] = {
                    x: rect.left + rect.width / 2 - containerRect.left,
                    y: 0,
                };
            }

            this.navLinkPositions = positions;
            this.positionsCalculated = true;
        },

        animateToRoute(animate = true) {
            const route = this.$route.path;
            const indicator = this.$refs.indicator;

            if (!indicator) return;

            let positionKey = route;
            if (this.isMobile && this.isCurrentRouteInDrawer) {
                positionKey = '__more__';
            }

            const targetPosition = this.navLinkPositions[positionKey];

            if (!targetPosition) {
                if (!this.positionsCalculated) {
                    this.calculateNavPositions();
                    const newTarget = this.navLinkPositions[positionKey];
                    if (newTarget) {
                        this.performAnimation(indicator, newTarget, animate);
                    }
                }
                return;
            }

            this.performAnimation(indicator, targetPosition, animate);
        },

        performAnimation(indicator, targetPosition, animate) {
            if (this.isMobile) {
                const indicatorWidth = indicator.offsetWidth || 40;
                const newX = targetPosition.x - indicatorWidth / 2;

                if (animate) {
                    requestAnimationFrame(() => {
                        gsap.to(indicator, {
                            x: newX,
                            y: 0,
                            duration: 0.5,
                            ease: getSpringEase(),
                            onComplete: () => {
                                this.currentPosition = { x: newX, y: 0 };
                            },
                        });
                        gsap.set(indicator, { transformOrigin: 'center center' });
                    });
                } else {
                    gsap.set(indicator, { x: newX, y: 0, transformOrigin: 'center center' });
                    this.currentPosition = { x: newX, y: 0 };
                }
            } else {
                const newY = targetPosition.y;
                const currentY = this.currentPosition.y;
                const direction = currentY > newY ? 'up' : 'down';

                if (animate) {
                    requestAnimationFrame(() => {
                        gsap.to(indicator, {
                            y: newY,
                            duration: 0.5,
                            ease: getSpringEase(),
                            onComplete: () => {
                                this.currentPosition = { x: 0, y: newY };
                            },
                        });
                        gsap.set(indicator, {
                            transformOrigin: direction === 'up' ? 'top center' : 'bottom center',
                        });
                    });
                } else {
                    gsap.set(indicator, {
                        y: newY,
                        transformOrigin: direction === 'up' ? 'top center' : 'bottom center',
                    });
                    this.currentPosition = { x: 0, y: newY };
                }
            }
        },

        handleLayoutChange() {
            this.positionsCalculated = false;
            const indicator = this.$refs.indicator;

            if (indicator) {
                gsap.set(indicator, { x: 0, y: 0 });
                this.currentPosition = { x: 0, y: 0 };
            }

            setTimeout(() => {
                this.$nextTick(() => {
                    this.calculateNavPositions();
                    this.animateToRoute();
                });
            }, 300);
        },

        recalculate() {
            this.positionsCalculated = false;
            this.calculateNavPositions();
            this.animateToRoute();
        },

        getNavContainer() {
            let parent = this.$el?.parentElement;
            while (parent) {
                if (
                    parent.classList?.contains('AppNavigation') ||
                    parent.classList?.contains('MobileNavBar') ||
                    parent.classList?.contains('Wrapper')
                ) {
                    return parent;
                }
                parent = parent.parentElement;
            }
            return this.$el?.closest('.AppNavigation') || this.$el?.closest('.Wrapper');
        },
    },
};
</script>

<style lang="scss" scoped>
.NavigationIndicator {
    position: absolute;
    top: 0;
    left: 0;
    background: var(--color-bg);
    height: 24px;
    width: 10px;
    will-change: transform;
    border-radius: 2px;
    box-shadow: 0 0 8px rgba(255, 255, 255, 0.3);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    pointer-events: none;

    &.is-mobile {
        width: 40px;
        height: 5px;
        z-index: 1000;
        border-radius: 0 0 3px 3px;
        box-shadow: 0 0 12px rgba(255, 255, 255, 0.4);
    }
}
</style>
